import React, { useEffect, useRef } from 'react';
import LicenseExpiredOverlay from './LicenseExpiredOverlay';
import type { LicenseGate as LicenseGateVerdict } from './useLicenseGate';

interface LicenseGateProps {
  /** The verdict from `useLicenseGate`. `null` renders children untouched. */
  gate: LicenseGateVerdict | null;
  /**
   * Position the blurred backdrop absolutely instead of in normal flow. Needed by full-bleed
   * surfaces like the BGP Visibility canvas, whose children size themselves against the
   * container rather than their own content; page content in the detail tabs must stay in flow
   * so it is not clipped to the viewport.
   */
  fullBleed?: boolean;
  children: React.ReactNode;
}

/**
 * Wraps a view's own content in the license gate (CTX-346).
 *
 * When `gate` is null this is a pass-through, so an ungated view carries no extra wrapper in
 * its tree. When gated, the content stays drawn — blurred, dimmed and inert — behind the
 * explanatory card, following the CTX-125 precedent: the operator can see the shape of what was
 * there, but it reads as unverifiable last-known state rather than live data.
 *
 * Composing both halves here — rather than at each call site — is deliberate: six mount points
 * across four views would otherwise drift on the blur radius, the z-index or the a11y contract.
 */
export const LicenseGate: React.FC<LicenseGateProps> = ({ gate, fullBleed, children }) => {
  const backdropRef = useRef<HTMLDivElement>(null);
  const isGated = Boolean(gate);

  // `pointer-events-none` stops clicks and scrolls but NOT the tab order — a keyboard user
  // would still walk into the blurred buttons and inputs, which the gate must prevent. `inert`
  // takes the whole subtree out of focus, hit-testing and the a11y tree in one move; it is set
  // imperatively because React 18 does not accept it as a JSX prop (React 19 added it).
  // The classes below stay regardless: they are the visual treatment, and the fallback on the
  // rare engine without `inert`.
  useEffect(() => {
    const node = backdropRef.current;
    if (!node) {
      return;
    }
    if (isGated) {
      node.setAttribute('inert', '');
    } else {
      node.removeAttribute('inert');
    }
  }, [isGated]);

  if (!gate) {
    return <>{children}</>;
  }

  return (
    <div className="relative h-full w-full">
      <div
        ref={backdropRef}
        className={`pointer-events-none select-none opacity-60 blur-[3px] ${
          fullBleed ? 'absolute inset-0' : ''
        }`}
        aria-hidden
      >
        {children}
      </div>
      <LicenseExpiredOverlay
        subject={gate.subject}
        reason={gate.reason}
        endDate={gate.endDate}
      />
    </div>
  );
};

export default LicenseGate;
